import { getConditionDisplayName } from '../config/skinConditionDisplayNames';
import { normalizeConditionName } from './skinConditionNormalization';
import {
  CLASS_COLORS_MAP,
  canonicalSkinConditionName,
  getColorByClassId,
  isSkinConditionName,
} from './skinResultsUtils';

const CONDITION_MAPPING = {
  hydration: 'Hydration',
  oxygen: 'Oxygen',
  smoothness: 'Smoothness',
  dullness: 'Dullness',
  uneven_skintone: 'UnevenSkin',
};

const MIN_OVERLAPPING_CONCERNS = 2;

function parseNumericScore(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  if (typeof raw === 'object') {
    const nested = raw.score ?? raw.value ?? raw.percentage ?? raw.percent;
    return parseNumericScore(nested);
  }
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

function nameAllowedBySegmentation(name, segmentationAllowedNames) {
  if (segmentationAllowedNames === null) return true;
  const canonical = canonicalSkinConditionName(name) || name;
  if (segmentationAllowedNames.has(name) || segmentationAllowedNames.has(canonical)) return true;
  for (const allowed of segmentationAllowedNames) {
    if ((canonicalSkinConditionName(allowed) || allowed) === canonical) return true;
  }
  return false;
}

function resolveConcernKey(rawName) {
  const normalized = normalizeConditionName(String(rawName || '').trim());
  return canonicalSkinConditionName(normalized) || canonicalSkinConditionName(rawName) || normalized;
}

/**
 * Build unified concern items (same merge rules for current scan and previous snapshot).
 */
export function buildConcernItemsList({
  detections = [],
  skinConditions = {},
  labelScores = null,
  showSkinConcerns = true,
  showSkinConditions = true,
  segmentationAllowedNames = null,
  visibleConditionNames = null,
} = {}) {
  const groupedDetections = (Array.isArray(detections) ? detections : []).reduce((acc, det) => {
    const raw = det.class_name || det.className || 'Unknown';
    const className = resolveConcernKey(raw);
    if (!className || className === 'Unknown' || className === 'Mole' || det.class_id === 7 || det.class_id === 8) {
      return acc;
    }
    if (!showSkinConcerns && isSkinConditionName(className)) return acc;
    if (!showSkinConditions && !isSkinConditionName(className)) return acc;
    if (!nameAllowedBySegmentation(className, segmentationAllowedNames)) return acc;
    const scoreNum = det.score !== undefined ? Number(det.score) : 100;
    const score = Number.isNaN(scoreNum) ? 100 : scoreNum;
    if (!acc[className] || score < acc[className].score) {
      acc[className] = { class_name: className, score, class_id: det.class_id, confidences: [], count: 0 };
    }
    const confidence = det.confidence || det.confidence_score || 0;
    acc[className].confidences.push(confidence);
    acc[className].count += 1;
    return acc;
  }, {});

  const detectionItems = Object.values(groupedDetections).map((g) => {
    const avgConfidence = (g.confidences || []).length
      ? g.confidences.reduce((s, c) => s + c, 0) / g.confidences.length
      : 1;
    return {
      class_name: g.class_name,
      score: Math.round(g.score),
      confidence: avgConfidence,
      count: g.count,
      color: CLASS_COLORS_MAP[g.class_name] || getColorByClassId(g.class_id),
      class_id: g.class_id,
      is_condition: false,
    };
  });

  const conditionItems = [];
  Object.entries(CONDITION_MAPPING).forEach(([key, displayName]) => {
    if (!showSkinConcerns) return;
    if (!nameAllowedBySegmentation(displayName, segmentationAllowedNames)) return;
    const entry = skinConditions?.[key];
    const n = parseNumericScore(typeof entry === 'object' ? entry?.score : entry);
    if (n == null) return;
    conditionItems.push({
      class_name: displayName,
      score: Math.round(n),
      confidence: 1,
      count: 1,
      color: CLASS_COLORS_MAP[displayName] || 'rgb(140, 80, 200)',
      class_id: null,
      is_condition: true,
    });
  });

  const byName = {};
  [...detectionItems, ...conditionItems].forEach((it) => { byName[it.class_name] = it; });

  if (labelScores && typeof labelScores === 'object') {
    Object.entries(labelScores).forEach(([rawName, rawScore]) => {
      const name = resolveConcernKey(rawName);
      const n = parseNumericScore(rawScore);
      if (!name || n == null) return;
      if (!showSkinConcerns && isSkinConditionName(name)) return;
      if (!showSkinConditions && !isSkinConditionName(name)) return;
      if (!nameAllowedBySegmentation(name, segmentationAllowedNames)) return;
      const existing = byName[name];
      if (existing) {
        existing.score = Math.round(Math.min(existing.score, n));
        return;
      }
      byName[name] = {
        class_name: name,
        score: Math.round(n),
        confidence: 1,
        count: 1,
        color: CLASS_COLORS_MAP[name] || 'rgb(140, 80, 200)',
        class_id: null,
        is_condition: isSkinConditionName(name),
      };
    });
  }

  let allItems = Object.values(byName).sort((a, b) => a.score - b.score);

  if (Array.isArray(visibleConditionNames) && visibleConditionNames.length > 0) {
    const allowedCanonical = new Set(
      visibleConditionNames.map((n) => canonicalSkinConditionName(n) || n),
    );
    allItems = allItems.filter((d) => {
      const c = canonicalSkinConditionName(d.class_name) || d.class_name;
      return allowedCanonical.has(c) || visibleConditionNames.includes(d.class_name);
    });
  }

  const severity = allItems.filter((d) => d.score < 75);
  const good = allItems.filter((d) => d.score >= 75);
  return { severity, good, allItems };
}

export function buildConcernRadarItemsFromList(allItems = []) {
  return [...allItems]
    .sort((a, b) => a.score - b.score)
    .map((item) => ({
      ...item,
      label: getConditionDisplayName(item.class_name),
      intensity: Math.max(5, Math.min(100, Number(item.score || 0))),
    }));
}

export function previousPayloadToSources(previousSkinData) {
  if (!previousSkinData || typeof previousSkinData !== 'object') return null;
  const prevScoreObj = previousSkinData.skinScore ?? previousSkinData.skin_score;
  const labelScores = prevScoreObj?.skin_label_scores ?? previousSkinData.skin_label_scores ?? null;
  const rawConditions = previousSkinData.skinConditions ?? previousSkinData.condition_scores ?? {};
  const skinConditions = {};
  if (rawConditions && typeof rawConditions === 'object') {
    Object.entries(rawConditions).forEach(([key, value]) => {
      if (value && typeof value === 'object' && value.score != null) {
        skinConditions[key] = value;
      } else {
        const n = parseNumericScore(value);
        if (n != null) skinConditions[key] = { score: n };
      }
    });
  }

  const detectionScores = previousSkinData.detection_scores;
  const detections = Array.isArray(previousSkinData.detections) && previousSkinData.detections.length
    ? previousSkinData.detections
    : Array.isArray(detectionScores)
      ? detectionScores.map((d) => ({
        class_name: d.class_name || d.className,
        class_id: d.class_id ?? d.classId,
        score: d.score,
        confidence: d.confidence ?? d.confidence_score,
      }))
      : [];

  return { detections, skinConditions, labelScores };
}

export function buildConcernItemsFromPreviousPayload(previousSkinData, options = {}) {
  const sources = previousPayloadToSources(previousSkinData);
  if (!sources) return { severity: [], good: [], allItems: [] };
  return buildConcernItemsList({ ...sources, ...options });
}

export function countOverlappingConcerns(currentItems = [], previousItems = []) {
  const prevKeys = new Set(
    previousItems.map((item) => resolveConcernKey(item.class_name)).filter(Boolean),
  );
  return currentItems.filter((item) => prevKeys.has(resolveConcernKey(item.class_name))).length;
}

export function isPreviousScanComparable(previousSkinData, currentItems = [], previousItems = [], currentDateRaw = null) {
  if (!previousSkinData || typeof previousSkinData !== 'object') return false;
  if (!previousItems.length || !currentItems.length) return false;

  const overlap = countOverlappingConcerns(currentItems, previousItems);
  if (overlap < MIN_OVERLAPPING_CONCERNS) return false;

  const previousDateRaw = previousSkinData.created_at
    || previousSkinData.analysis_date
    || previousSkinData.createdAt
    || previousSkinData._snapshot_at;
  if (previousDateRaw && currentDateRaw) {
    const prevTime = new Date(previousDateRaw).getTime();
    const currentTime = new Date(currentDateRaw).getTime();
    if (!Number.isNaN(prevTime) && !Number.isNaN(currentTime) && prevTime >= currentTime) {
      return false;
    }
  }

  return true;
}

export { MIN_OVERLAPPING_CONCERNS, resolveConcernKey };
